import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { audioUrl } from "@/lib/staticApi";
import { toast } from "sonner";
import AvatarOverlay from "@/components/AvatarOverlay";

// Cloudflare Worker endpoint donde vive "/guest-login".
// (Es el mismo Worker de "Pregúntale a Papi" — permite CORS abierto.)
const GUEST_LOGIN_URL = "https://preguntale-a-papi.guendelman.workers.dev/guest-login";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [members, setMembers] = useState([]);
  const [member, setMember] = useState(() => localStorage.getItem("rec_last_member") || "");
  const [guestName, setGuestName] = useState("");
  const [password, setPassword] = useState("");
  const [kosher, setKosher] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recipeCount, setRecipeCount] = useState(null);
  const [welcomeVideo, setWelcomeVideo] = useState(null); // URL mp4 en curso

  useEffect(() => {
    fetch("/data/members.json")
      .then((r) => r.json())
      .then((data) => setMembers(data || []))
      .catch(() => setMembers([]));
    // Conteo real de recetas (lee el JSON publicado, fuente de verdad).
    fetch("/data/recipes.json")
      .then((r) => r.json())
      .then((data) => setRecipeCount(Array.isArray(data) ? data.length : null))
      .catch(() => setRecipeCount(null));
  }, []);

  // Cuando cambia el miembro seleccionado, limpiamos el nombre del invitado
  // para evitar arrastres accidentales.
  useEffect(() => {
    if (member !== "invitado") setGuestName("");
  }, [member]);

  const playGuestWelcome = async (name) => {
    // Llama al Cloudflare Worker: genera el saludo con la voz PVC del Pollo
    // y en paralelo dispara el mail de aviso a Alan (vía Apps Script).
    // Primera vez del invitado → saludo largo. Siguientes → saludo corto.
    let returning = false;
    let seenKey = "";
    try {
      const normalized = name.trim().toLowerCase().replace(/\s+/g, "-");
      seenKey = `recetario_guest_seen_${normalized}`;
      returning = localStorage.getItem(seenKey) === "1";
    } catch { /* ignore */ }
    try {
      const res = await fetch(GUEST_LOGIN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          returning,
          userAgent: navigator.userAgent || "",
          when: new Date().toISOString(),
        }),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data?.audio_url) {
        const audio = new Audio(data.audio_url);
        audio.play().catch(() => { /* autoplay bloqueado, no importa */ });
      }
      // Marca como visto solo si el saludo se generó ok
      if (seenKey) {
        try { localStorage.setItem(seenKey, "1"); } catch { /* silent */ }
      }
    } catch { /* offline u otro error: seguimos sin audio */ }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!member) { toast.error("Elige quién eres"); return; }
    if (member === "invitado" && !guestName.trim()) {
      toast.error("Cuéntanos tu nombre");
      return;
    }
    setBusy(true);
    try {
      await login(password, member, guestName.trim(), kosher);
      localStorage.setItem("rec_last_member", member);

      if (member === "invitado") {
        // Saludo dinámico con voz PVC + aviso por mail (fire-and-forget).
        toast.success(`¡Hola, ${guestName.trim()}! Bienvenido al recetario.`);
        playGuestWelcome(guestName.trim());
        nav("/cocina", { replace: true });
      } else {
        // Welcome audio: primera vez del miembro en este dispositivo → saludo LARGO.
        // Siguientes veces → saludo CORTO ("qué bueno que volviste, disfruta").
        //
        // Sistema de VERSIONES: si cambiamos el saludo largo de un miembro,
        // subimos su número en WELCOME_VERSIONS y los que ya lo habían visto
        // vuelven a escuchar el largo (una vez), luego pasan al corto.
        const WELCOME_VERSIONS = { tairuchi: 2 };
        const key = `recetario_welcome_seen_${member}`;
        const currentV = WELCOME_VERSIONS[member] || 1;
        const seenV = parseInt(localStorage.getItem(key) || "0", 10);
        const firstTime = seenV < currentV;
        const suffix = firstTime ? "" : "_short";
        const videoPath = `/avatars/videos/welcome_${member}${suffix}.mp4`;
        try { localStorage.setItem(key, String(currentV)); } catch { /* silent */ }
        // Mostramos el overlay con el video (trae audio). Al terminar navega.
        // Si el video no existe (404) → fallback audio MP3 tradicional.
        setWelcomeVideo(videoPath);
        // Timeout de seguridad: si algo falla, nav después de 30s
        setTimeout(() => nav("/cocina", { replace: true }), 30000);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "No pudimos entrar");
    } finally {
      setBusy(false);
    }
  };

  const currentLabel = () => {
    if (!member) return "Elige quién eres";
    if (member === "invitado") {
      return guestName.trim() ? `Entrar como ${guestName.trim()}` : "Entrar como invitado";
    }
    return `Entrar como ${members.find((x) => x.id === member)?.label || member}`;
  };

  return (
    <div className="min-h-screen paper-texture flex flex-col items-center justify-center px-6 iphone-safe-top">
      <AvatarOverlay
        videoUrl={welcomeVideo}
        size="lg"
        onEnded={() => nav("/cocina", { replace: true })}
        onFallback={() => {
          // Video 404 o autoplay bloqueado → reproducimos el MP3 tradicional
          // y navegamos inmediatamente.
          try {
            const suffix = welcomeVideo?.includes("_short") ? "welcome_short" : "welcome";
            const audio = new Audio(audioUrl(member, suffix));
            audio.play().catch(() => { /* silent */ });
          } catch { /* silent */ }
          setWelcomeVideo(null);
          nav("/cocina", { replace: true });
        }}
      />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="relative mx-auto mb-5 w-40 h-40 sm:w-48 sm:h-48" data-testid="login-pollo-photo">
            <div className="absolute inset-0 rounded-full bg-amber-deep/20 blur-2xl" aria-hidden="true" />
            <LoginHeroAvatar />
          </div>
          <p className="eyebrow mb-3">Familia Guendelman</p>
          <h1 className="font-display text-cocoa text-5xl sm:text-6xl leading-none italic font-semibold mb-2">El Recetario<br /><span className="text-amber-deep">del Pollo</span></h1>
          <p data-testid="recipe-count" className="font-serif text-cocoa-soft text-lg italic mt-4">{recipeCount ?? 299} recetas que pasamos en casa.</p>
        </div>

        <form onSubmit={onSubmit} className="bg-cream/80 border border-line rounded-lg p-6 sm:p-7 shadow-[0_20px_40px_-22px_rgba(58,26,5,0.35)]">
          <span className="eyebrow">¿Quién está cocinando?</span>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:gap-3">
            {members.filter((m) => m.id !== "invitado").map((m) => (
              <button
                key={m.id}
                type="button"
                data-testid={`member-pick-${m.id}`}
                onClick={() => setMember(m.id)}
                className={`px-3 py-3 rounded-md font-serif text-lg transition ${
                  member === m.id
                    ? "bg-amber-deep text-cream border-2 border-amber-deep"
                    : "bg-paper border-2 border-line text-cocoa hover:border-amber-deep"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {/* Botón especial "Familia & Amigos" — full width para diferenciarlo. */}
          {members.some((m) => m.id === "invitado") && (
            <button
              type="button"
              data-testid="member-pick-invitado"
              onClick={() => setMember("invitado")}
              className={`mt-3 w-full px-3 py-3 rounded-md font-serif text-lg italic transition ${
                member === "invitado"
                  ? "bg-amber-deep text-cream border-2 border-amber-deep"
                  : "bg-paper border-2 border-dashed border-amber-deep/60 text-amber-deep hover:bg-amber-deep/10"
              }`}
            >
              Familia &amp; Amigos
            </button>
          )}

          {/* Input de nombre — solo para invitados */}
          {member === "invitado" && (
            <label className="block mt-5">
              <span className="eyebrow">Nombre:</span>
              <input
                data-testid="guest-name-input"
                type="text"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                required
                autoFocus
                maxLength={40}
                autoCorrect="off"
                autoCapitalize="words"
                spellCheck={false}
                className="mt-2 w-full px-4 py-3 bg-paper border border-line rounded-md font-serif text-lg text-cocoa focus:outline-none focus:ring-2 focus:ring-amber-deep/40"
                placeholder="Tu nombre"
              />
            </label>
          )}

          <label className="block mt-5">
            <span className="eyebrow">Tu contraseña</span>
            <input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus={member !== "invitado"}
              autoCorrect="off"
              autoCapitalize="none"
              autoComplete="new-password"
              spellCheck={false}
              inputMode="text"
              className="mt-2 w-full px-4 py-3 bg-paper border border-line rounded-md font-serif text-lg text-cocoa focus:outline-none focus:ring-2 focus:ring-amber-deep/40 tracking-widest"
              placeholder="••••"
            />
          </label>

          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={busy || !member || (member === "invitado" && !guestName.trim())}
            className="btn-primary w-full mt-6 disabled:opacity-50"
          >
            {busy ? "Entrando…" : currentLabel()}
          </button>

          {/* Toggle Kosher — visible cuando ya se eligió miembro */}
          {member && (
            <label className="flex items-center gap-3 mt-5 cursor-pointer select-none" data-testid="kosher-toggle-label">
              <input
                data-testid="kosher-toggle-checkbox"
                type="checkbox"
                checked={kosher}
                onChange={(e) => setKosher(e.target.checked)}
                className="h-5 w-5 accent-amber-deep flex-shrink-0"
              />
              <span className="font-serif text-cocoa text-base">
                🕎 Respeta comida <b>Kosher</b>
              </span>
            </label>
          )}

          <p className="text-center mt-5 text-sm text-cocoa-soft font-serif italic">
            Cada uno tiene su propia clave. Si la olvidaste, pídele a Papi.
          </p>
        </form>
      </div>
    </div>
  );
}

// Avatar hero del login: muestra el video "Bienvenido al Recetario del Pollo,
// elige tu usuario" si existe (welcome_intro.mp4). Si no carga, cae al JPG.
// El video arranca MUTED (autoplay permitido) y se desmutea automáticamente
// apenas el usuario interactúa con la página (toca cualquier lugar / botón).
function LoginHeroAvatar() {
  const videoRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [unmuted, setUnmuted] = useState(false);
  const commonRing = "relative w-full h-full rounded-full object-cover shadow-[0_18px_38px_-18px_rgba(58,26,5,0.55)] ring-4 ring-amber-deep/80 ring-offset-4 ring-offset-paper";

  // Intento desmutear apenas hay un tap/click en cualquier parte del document
  useEffect(() => {
    if (failed || unmuted) return;
    const tryUnmute = () => {
      const v = videoRef.current;
      if (!v) return;
      v.muted = false;
      v.currentTime = 0;
      const p = v.play();
      if (p && p.then) {
        p.then(() => setUnmuted(true)).catch(() => { /* siguió muted */ });
      }
    };
    const opts = { once: true, passive: true };
    document.addEventListener("click", tryUnmute, opts);
    document.addEventListener("touchstart", tryUnmute, opts);
    return () => {
      document.removeEventListener("click", tryUnmute);
      document.removeEventListener("touchstart", tryUnmute);
    };
  }, [failed, unmuted]);

  if (failed) {
    return (
      <img
        src="/alan_chef.jpg"
        alt="Papi en la cocina"
        loading="eager"
        className={commonRing}
        style={{ objectPosition: "62% 35%" }}
      />
    );
  }
  return (
    <>
      {!loaded && (
        <img
          src="/alan_chef.jpg"
          alt="Papi en la cocina"
          className={commonRing + " absolute inset-0"}
          style={{ objectPosition: "62% 35%" }}
        />
      )}
      <video
        ref={videoRef}
        src="/avatars/videos/welcome_intro.mp4"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        className={commonRing + " " + (loaded ? "" : "opacity-0")}
        style={{ objectPosition: "62% 35%" }}
        onCanPlay={() => setLoaded(true)}
        onError={() => setFailed(true)}
      />
      {!unmuted && loaded && (
        <button
          type="button"
          data-testid="login-unmute"
          onClick={() => {
            const v = videoRef.current;
            if (!v) return;
            v.muted = false;
            v.currentTime = 0;
            v.play().then(() => setUnmuted(true)).catch(() => { /* silent */ });
          }}
          className="absolute bottom-1 right-1 w-9 h-9 rounded-full bg-amber-deep text-cream shadow-lg flex items-center justify-center text-lg animate-pulse"
          aria-label="Reproducir con sonido"
        >
          🔊
        </button>
      )}
    </>
  );
}


